// ---------------------------------------------------------------------------
// 店舗 予算作成ページ API (2026-06-12)
//   原本Excel(損益計算書/労務費/明細)のドライバーモデルを踏襲:
//     入力 = 原価率・営業利益目標(月別) + 労務費(社員給与/平均時給×勤務時間=雑給/法定福利費/福利厚生費/通勤費/募集費/研修費)
//            + 販売費(勘定別月別)。
//     派生 = 売上高=(一般管理費+営業利益)/(1-原価率)、原価=売上高×原価率、売上総利益/損益分岐点。
//   前年同月実績は freee(期-1)から自動プリフィル(budgetActual.ts の fetchTrial 流用)。
//   確定値は budget_monthly(source='budget_create')へ materialize → 予実対比表が無改修で読む。
// ---------------------------------------------------------------------------

import { fetchTrial } from './budgetActual';
import { BUDGET_LAYOUT, LABOR_LABELS, SELLING_LABELS } from '../data/budgetActualLayout';
import { fiscalMonthToCalendar, lastDayOfMonth } from '../types/reports';

const API_BASE = 'https://invoice-review.system-0f7.workers.dev';

export type Half = 'H1' | 'H2';
export function monthsForHalf(half: Half): number[] {
  return half === 'H1' ? [1, 2, 3, 4, 5, 6] : [7, 8, 9, 10, 11, 12];
}
// 会計月(1=10月) → 表示用「N月度（暦月）」
export const FISCAL_MONTH_CAL = ['10月', '11月', '12月', '1月', '2月', '3月', '4月', '5月', '6月', '7月', '8月', '9月'];

// budgetItem(=display_item) → freeeName(前年実績の引当に使う leaf 名)
const ITEM_TO_FREEE = new Map<string, string>();
for (const r of BUDGET_LAYOUT) {
  if (r.budgetItem && r.freeeName) ITEM_TO_FREEE.set(r.budgetItem, r.freeeName);
}
// 予算作成で扱う display_item 群(budgetItem を持つ行のみ)
const budgetableLabel = (label: string) => BUDGET_LAYOUT.some((r) => r.label === label && r.budgetItem);
// 労務費(直接入力)= 人件費群 のうち予算対象。雑給は派生(時給×時間)なので別扱い。
export const LABOR_DIRECT_ITEMS = LABOR_LABELS.filter((l) => l !== '雑給' && budgetableLabel(l)); // 給与手当/法定福利費/福利厚生費/通勤費/募集費/研修費
export const SELLING_ITEMS = SELLING_LABELS.filter((l) => budgetableLabel(l)); // 16 勘定

export interface BudgetInputRow { input_key: string; fiscal_month: number; value: number; note?: string | null }
export interface BudgetMonthlyRow { display_item: string; fiscal_month: number; amount: number }
export interface BudgetCreateData {
  fiscal_term: number; section_id: number; half: Half; months: number[];
  inputs: BudgetInputRow[];
  budget: BudgetMonthlyRow[];
  submission: { status: string; version: number; submitted_by?: string | null; submitted_at?: string | null };
}

async function req<T>(path: string, opts?: RequestInit): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, opts);
  if (!res.ok) { let b: any = null; try { b = await res.json(); } catch { /* */ } throw new Error(b?.error || b?.message || `${res.status}`); }
  return res.json();
}

export function fetchBudgetCreate(term: number, sectionId: number, half: Half): Promise<BudgetCreateData> {
  return req(`/api/budget/create?fiscal_term=${term}&section_id=${sectionId}&half=${half}`);
}

export function saveBudgetInput(body: {
  fiscal_term: number; section_id: number; half: Half;
  inputs: BudgetInputRow[]; budget_rows: BudgetMonthlyRow[]; updated_by?: string;
}): Promise<{ ok: boolean; inputs_saved: number; budget_saved: number }> {
  return req(`/api/budget/input/save`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
}

export function submitBudget(term: number, sectionId: number, half: Half, by?: string): Promise<any> {
  return req(`/api/budget/submit`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ fiscal_term: term, section_id: sectionId, half, submitted_by: by || 'web_app_user' }) });
}

export function reopenBudget(term: number, sectionId: number, half: Half): Promise<any> {
  return req(`/api/budget/reopen`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ fiscal_term: term, section_id: sectionId, half }) });
}

/**
 * 前年同月実績の自動取込。期-1 の同じ半期の各月 trial_pl(section別)を取得し、
 * budgetable display_item ごとに前年実績(発生額)を返す。
 * @returns Map<display_item, Map<fiscal_month, amount>>
 */
export async function fetchPrevActual(term: number, sectionId: number, half: Half): Promise<Map<string, Map<number, number>>> {
  const months = monthsForHalf(half);
  const prevTerm = term - 1;
  const datas = await Promise.all(months.map((fm) => {
    const cal = fiscalMonthToCalendar(prevTerm, fm);
    const range = { start: `${cal.year}-${String(cal.month).padStart(2, '0')}-01`, end: lastDayOfMonth(cal.year, cal.month) };
    return fetchTrial('account_item', range, sectionId).then((d) => ({ fm, d }));
  }));
  const out = new Map<string, Map<number, number>>();
  const put = (item: string, fm: number, v: number) => {
    if (!out.has(item)) out.set(item, new Map());
    out.get(item)!.set(fm, v);
  };
  for (const { fm, d } of datas) {
    // 売上 = group「売上高」/ それ以外 = freeeName leaf
    put('売上', fm, d.groupTotals.get('売上高') || d.byName.get('売上高') || 0);
    ITEM_TO_FREEE.forEach((freeeName, item) => {
      if (item === '売上') return;
      put(item, fm, d.byName.get(freeeName) || 0);
    });
  }
  return out;
}
